import React from "react";
import { useNavigate } from "react-router-dom";
import { intl } from "LocaleWrapper";
import { useMutation, useQuery } from "@apollo/client";

import { Table, PageHeader, Card, Button, Tag, TagSize } from "@cofano/cck";
import { Stack } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";

import { TableId, useFrontendTable } from "components/table";
import { useConfirm } from "components/hooks/useConfirm";
import Helmet from "components/helmet/Helmet";

import { Language } from "imports/Utils";

import { FormattedDate } from "utils/DateUtils";

import { ChildPropsFromArray } from "types/commons";
import { ConnectingVisitStatus } from "__generated__/enums";
import { GetConnectingVisitsDocument, GetConnectingVisitsQuery } from "__generated__/view/connectingVisits/graphql/connectingVisit.queries.generated";
import { DeleteConnectingVisitDocument } from "__generated__/view/connectingVisits/graphql/connectingVisit.mutations.generated";

type ConnectingVisitFromQuery = ChildPropsFromArray<GetConnectingVisitsQuery, "connectingVisits">

const ConnectingVisits = () => {

	const confirm = useConfirm();
	const navigate = useNavigate();

	const { data, refetch, loading } = useQuery(GetConnectingVisitsDocument);

	const [ deleteConnectingVisit ] = useMutation(DeleteConnectingVisitDocument);

	const { tableProps, refetchAfterDelete } = useFrontendTable({
		id: TableId.ConnectingVisits,
		content: data?.connectingVisits ?? [],
	});

	const onDelete = (id: number) => {
		deleteConnectingVisit({
			variables: {
				id: id,
			},
		}).then(() => {
			refetchAfterDelete();
			refetch();
		});
	};

	return (
		<React.Fragment>
			<PageHeader>
				<PageHeader.Title>
					{Language("CONNECTING_VISITS")}
					<Helmet title={intl.formatMessage({ id: "CONNECTING_VISITS" })} />
				</PageHeader.Title>

				<PageHeader.Actions>
					<Button onClick={() => navigate("/view/connectingvisits/create")}>
						{Language("ADD_BUTTON")}
					</Button>
				</PageHeader.Actions>
			</PageHeader>

			<Card disablePadding>
				<Table
					{...tableProps}
					contentLoading={loading}
					columns={[ {
						id: "vessel",
						title: intl.formatMessage({ id: "CONNECTING_VESSEL" }),
						render: (row: ConnectingVisitFromQuery) => row.vessel?.name,
					}, {
						id: "location",
						title: intl.formatMessage({ id: "LOCATION" }),
						render: (row: ConnectingVisitFromQuery) => row.port?.description,
					}, {
						id: "callReferenceNumber",
						title: intl.formatMessage({ id: "CONNECTING_ORDER_CALL_REFERENCE_NUMBER" }),
					}, {
						id: "status",
						title: intl.formatMessage({ id: "STATUS" }),
						width: 150,
						render: (row: ConnectingVisitFromQuery) => {
							if (row.status === ConnectingVisitStatus.PreArrived) {
								return (
									<Tag
										color="#D18427"
										text={intl.formatMessage({ id: "CONNECTING_VISIT_STATUS_PRE_ARRIVED" })}
										size={TagSize.NORMAL} />
								);
							} else if (row.status === ConnectingVisitStatus.Arrived) {
								return (
									<Tag
										color="#45B741"
										text={intl.formatMessage({ id: "CONNECTING_VISIT_STATUS_ARRIVED" })}
										size={TagSize.NORMAL} />
								);
							} else if (row.status === ConnectingVisitStatus.Departed) {
								return (
									<Tag
										color="#3190D8"
										text={intl.formatMessage({ id: "CONNECTING_VISIT_STATUS_DEPARTED" })}
										size={TagSize.NORMAL} />
								);
							} else if (row.status === ConnectingVisitStatus.Cancelled) {
								return (
									<Tag
										color="#D23838"
										text={intl.formatMessage({ id: "CONNECTING_VISIT_STATUS_CANCELLED" })}
										size={TagSize.NORMAL} />
								);
							} else {
								return intl.formatMessage({ id: "UNKNOWN" });
							}
						},
					}, {
						id: "numLoading",
						title: intl.formatMessage({ id: "ROTATION_CALL_LOADING_AMOUNT" }),
						width: 150,
						render: (row: ConnectingVisitFromQuery) => {

							const total = Number(row.numLoadingGreen ?? 0) + Number(row.numLoadingOrange ?? 0) + Number(row.numLoadingRed ?? 0);

							return (
								<Stack direction="row" spacing={0.5}>
									<Tag
										color="#99D1A0"
										text={String(row.numLoadingGreen ?? 0)}
										size={TagSize.NORMAL} />

									<Tag
										color="#E18E21"
										text={String(row.numLoadingOrange ?? 0)}
										size={TagSize.NORMAL} />

									<Tag
										color="#EC5353"
										text={String(row.numLoadingRed ?? 0)}
										size={TagSize.NORMAL} />

									<Tag
										color="#BBBBBB"
										text={String(total)}
										size={TagSize.NORMAL} />
								</Stack>
							);
						},
					}, {
						id: "numDischarging",
						title: intl.formatMessage({ id: "ROTATION_CALL_DISCHARGING_AMOUNT" }),
						width: 150,
						render: (row: ConnectingVisitFromQuery) => {

							const total = Number(row.numDischargingGreen ?? 0) + Number(row.numDischargingOrange ?? 0) + Number(row.numDischargingRed ?? 0);

							return (
								<Stack direction="row" spacing={0.5}>
									<Tag
										color="#99D1A0"
										text={String(row.numDischargingGreen ?? 0)}
										size={TagSize.NORMAL} />

									<Tag
										color="#E18E21"
										text={String(row.numDischargingOrange ?? 0)}
										size={TagSize.NORMAL} />

									<Tag
										color="#EC5353"
										text={String(row.numDischargingRed ?? 0)}
										size={TagSize.NORMAL} />

									<Tag
										color="#BBBBBB"
										text={String(total)}
										size={TagSize.NORMAL} />
								</Stack>
							);
						},
					}, {
						id: "eta",
						title: intl.formatMessage({ id: "CONNECTING_VISITS_ETA" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.eta} withoutYear />,
					}, {
						id: "ata",
						title: intl.formatMessage({ id: "CONNECTING_VISITS_ATA" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.ata} withoutYear />,
					}, {
						id: "etd",
						title: intl.formatMessage({ id: "CONNECTING_VISITS_ETD" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.etd} withoutYear />,
					}, {
						id: "atd",
						title: intl.formatMessage({ id: "CONNECTING_VISITS_ATD" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.atd} withoutYear />,
					}, {
						id: "cargoOpening",
						title: intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_OPENING" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.cargoOpening} withoutYear />,
					}, {
						id: "cargoClosing",
						title: intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_CLOSING" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.cargoClosing} withoutYear />,
					}, {
						id: "cargoAvailableStart",
						title: intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_AVAILABLE_START" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.cargoAvailableStart} withoutYear />,
					}, {
						id: "cargoAvailableEnd",
						title: intl.formatMessage({ id: "CONNECTING_ORDER_CARGO_AVAILABLE_END" }),
						width: 130,
						render: (row: ConnectingVisitFromQuery) => <FormattedDate date={row.cargoAvailableEnd} withoutYear />,
					} ]}
					rowActions={[ {
						icon: <EditIcon />,
						onClick: (row: ConnectingVisitFromQuery) => navigate(`/view/connectingvisits/${row.id}/edit`),
					}, {
						icon: <DeleteIcon />,
						onClick: (row: ConnectingVisitFromQuery) => {
							confirm({
								type: "DELETE",
							}).then(() => {
								onDelete(row.id);
							});
						},
					} ]} />
			</Card>
		</React.Fragment>
	);
};

export default ConnectingVisits;
